import { computed, onMounted, ref } from 'vue'
import { useGlobalPermissionsStore } from '../stores/globalPermissions'
import { useUserStore } from '../stores/userStore'

export function usePermissions(userId = null) {
  const permissionsStore = useGlobalPermissionsStore()
  const userStore = useUserStore()
  
  // Use provided userId or current user
  const targetUserId = ref(userId || userStore.currentUser?.id)
  
  // Reactive permission checking
  const hasPermission = computed(() => (permission) => {
    return permissionsStore.hasPermission(targetUserId.value, permission)
  })
  
  const hasAnyPermission = computed(() => (permissions) => {
    return permissionsStore.hasAnyPermission(targetUserId.value, permissions)
  })
  
  const hasAllPermissions = computed(() => (permissions) => {
    return permissionsStore.hasAllPermissions(targetUserId.value, permissions)
  })
  
  // Component access checker
  const canAccessComponent = computed(() => (componentName, requiredPermissions = []) => {
    return permissionsStore.canAccessComponent(targetUserId.value, componentName, requiredPermissions)
  })
  
  // Permission-based role checks (no actual roles used)
  const isAdmin = computed(() => permissionsStore.hasPermission(targetUserId.value, 'admin.access') || permissionsStore.hasPermission(targetUserId.value, '*'))
  const isTrader = computed(() => permissionsStore.hasAnyPermission(targetUserId.value, ['trading.basic', 'trading.advanced']))
  const isClient = computed(() => permissionsStore.hasAnyPermission(targetUserId.value, ['fixtures.view', 'reports.basic']))
  
  // Permission groups for common UI components
  const canManageUsers = computed(() => hasAnyPermission.value(['users.create', 'users.edit', 'users.delete']))
  const canViewReports = computed(() => hasAnyPermission.value(['reports.basic', 'reports.advanced', 'analytics.view']))
  const canTrade = computed(() => hasAnyPermission.value(['trading.basic', 'trading.advanced']))
  const canManageFixtures = computed(() => hasAnyPermission.value(['fixtures.create', 'fixtures.edit', 'fixtures.delete']))
  const canAccessServer = computed(() => hasAnyPermission.value(['server.access', 'database.view', 'admin.access']))
  
  // Component-specific permissions
  const canAccessSecondaryMenu = computed(() => {
    return hasAnyPermission.value([
      'menu.secondary',
      'navigation.advanced',
      'admin.access'
    ])
  })
  
  const canAccessTickets = computed(() => {
    return hasAnyPermission.value([
      'tickets.view',
      'tickets.create',
      'support.access'
    ])
  })
  
  const canAccessTrading = computed(() => {
    return hasAnyPermission.value([
      'trading.basic',
      'trading.advanced',
      'markets.view'
    ])
  })
  
  const canAccessAnalytics = computed(() => {
    return hasAnyPermission.value([
      'analytics.view',
      'reports.basic',
      'reports.advanced'
    ])
  })
  
  // Loading states
  const loading = computed(() => permissionsStore.loading)
  const permissionsLoaded = computed(() => permissionsStore.permissionsLoaded)
  
  // User permissions list
  const userPermissions = computed(() => {
    return permissionsStore.getUserPermissions(targetUserId.value)
  })
  
  // Refresh permissions
  const refreshPermissions = async (forceRefresh = false) => {
    if (targetUserId.value) {
      return await permissionsStore.fetchUserPermissions(targetUserId.value, forceRefresh)
    }
  }
  
  // Switch user being checked (useful for admin panels)
  const switchUser = (newUserId) => {
    targetUserId.value = newUserId
  }
  
  // Initialize permissions on mount
  onMounted(async () => {
    if (targetUserId.value && !permissionsStore.getUserPermissions(targetUserId.value).length) {
      await refreshPermissions()
    }
  })
  
  return {
    // Core permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessComponent,
    
    // Role checks
    isAdmin,
    isTrader,
    isClient,
    
    // Permission groups
    canManageUsers,
    canViewReports,
    canTrade,
    canManageFixtures,
    canAccessServer,
    
    // Component-specific permissions
    canAccessSecondaryMenu,
    canAccessTickets,
    canAccessTrading,
    canAccessAnalytics,
    
    // State
    loading,
    permissionsLoaded,
    userPermissions,
    targetUserId,
    
    // Actions
    refreshPermissions,
    switchUser
  }
}

// Specific composable for current user permissions (most common use case)
export function useCurrentUserPermissions() {
  const userStore = useUserStore()
  return usePermissions(userStore.currentUser?.id)
}

// Specific composable for checking other users (admin use case)  
export function useUserPermissionChecker() {
  const permissionsStore = useGlobalPermissionsStore()
  
  return {
    checkUserPermission: (userId, permission) => permissionsStore.hasPermission(userId, permission),
    checkUserAnyPermission: (userId, permissions) => permissionsStore.hasAnyPermission(userId, permissions),
    checkUserAllPermissions: (userId, permissions) => permissionsStore.hasAllPermissions(userId, permissions),
    fetchUserPermissions: (userId, forceRefresh = false) => permissionsStore.fetchUserPermissions(userId, forceRefresh),
  }
} 